import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';

const DietitianFourways = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Functional Foodie - Dietitian Serving Fourways",
    image: "https://functionalfoodie.co.za/lovable-uploads/24972437-aa8b-4772-a032-7db8f669e478.png",
    url: "https://functionalfoodie.co.za/dietitian-fourways",
    telephone: "+27783515631",
    address: {
      "@type": "PostalAddress",
      streetAddress: "48 Tanced Road",
      addressLocality: "Glenhazel",
      addressRegion: "Gauteng",
      postalCode: "2192",
      addressCountry: "ZA",
    },
    areaServed: [
      { "@type": "Place", name: "Fourways" },
      { "@type": "Place", name: "Bryanston" },
      { "@type": "Place", name: "Douglasdale" },
      { "@type": "Place", name: "Cedar Lakes" },
      { "@type": "Place", name: "Magaliessig" },
      { "@type": "Place", name: "Witkoppen" },
    ],
    medicalSpecialty: "Dietetics",
  };

  return (
    <div>
      <Helmet>
        <title>Dietitian in Fourways | Functional Foodie Registered Dietitians</title>
        <meta
          name="description"
          content="Registered dietitians serving Fourways, Bryanston, Douglasdale, Cedar Lakes and Witkoppen. Personalised nutrition counselling, gut health, weight management and chronic disease support. Medical aid rates apply."
        />
        <link rel="canonical" href="https://functionalfoodie.co.za/dietitian-fourways" />
        <meta property="og:title" content="Dietitian in Fourways | Functional Foodie" />
        <meta property="og:description" content="Registered dietitians serving Fourways and surrounding suburbs. Book a consultation today." />
        <meta property="og:url" content="https://functionalfoodie.co.za/dietitian-fourways" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[70vh] bg-[url('https://images.unsplash.com/photo-1490818387583-1baba5e638af?q=80&w=1932&auto=format&fit=crop')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative container mx-auto px-4 h-full flex flex-col items-center justify-center text-center pt-20">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Dietitian in <span className="text-secondary">Fourways</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl">
            Trusted registered dietitians serving Fourways, Bryanston, Douglasdale, Cedar Lakes and Witkoppen with personalised nutrition care.
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-secondary hover:bg-secondary-light text-secondary-foreground text-lg px-8 rounded-full">
              BOOK A CONSULTATION
            </Button>
          </Link>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Personalised Nutrition for Fourways Residents</h2>
          <p className="text-lg text-primary/80 leading-relaxed mb-4">
            Looking for a registered dietitian in Fourways? Functional Foodie offers expert nutrition consultations to clients across Fourways, Bryanston, Douglasdale, Cedar Lakes, Magaliessig, Witkoppen and the broader northern Johannesburg area. Our practice is based in Glenhazel, with both in-person and virtual consultations available.
          </p>
          <p className="text-lg text-primary/80 leading-relaxed mb-4">
            Whether you are managing a chronic condition, working on sustainable weight management, navigating pregnancy nutrition or seeking gut health support, we provide evidence-based guidance tailored to your goals. Medical aid rates apply, and we work with all major South African medical schemes.
          </p>
          <p className="text-lg text-primary/80 leading-relaxed">
            Fourways clients can visit us in Glenhazel or book a virtual appointment for the convenience of meeting from home or the office.
          </p>
        </div>
      </section>

      {/* Services for this location */}
      <section className="py-16 bg-accent/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-12">Services Available to Fourways Clients</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { id: "nutrition-counselling", label: "Nutrition Counselling" },
              { id: "gut-health", label: "Gut Health & IBS" },
              { id: "weight-management", label: "Weight Management" },
              { id: "sports-nutrition", label: "Sports Nutrition" },
              { id: "chronic-diseases", label: "Chronic Disease Support" },
              { id: "lifecycle-nutrition", label: "Lifecycle Nutrition" },
            ].map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow text-primary font-semibold text-lg text-center"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to book your Fourways dietitian appointment?</h2>
          <p className="text-lg mb-8 text-accent">In-person consultations in Glenhazel or virtual sessions from anywhere in Fourways.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-secondary hover:bg-secondary-light text-secondary-foreground text-lg px-8 rounded-full">
                BOOK ONLINE
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-lg px-8 rounded-full border-white text-white hover:bg-white hover:text-primary">
                CONTACT US
              </Button>
            </Link>
          </div>
          <p className="mt-8 text-sm text-accent">
            Also serving: <Link to="/dietitian-sandton" className="underline hover:text-secondary">Dietitian in Sandton</Link>, <Link to="/dietitian-randburg" className="underline hover:text-secondary">Dietitian in Randburg</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default DietitianFourways;
